// src/pages/DeliveryNotes/DnPrintTemplate.jsx
import React from "react";
import "./DnPrintTemplate.css";

const ITEMS_PER_PAGE = 40;

function formatDate(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("en-PH");
}

function getDatePart(value, part) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  if (part === "day") {
    return String(date.getDate()).padStart(2, "0");
  }

  if (part === "month") {
    return String(date.getMonth() + 1).padStart(2, "0");
  }

  if (part === "year") {
    return String(date.getFullYear()).slice(-2);
  }

  return "";
}

function groupDeliveryItems(items) {
  const map = new Map();

  (items || []).forEach((item) => {
    const catalogNumber = item.CatalogNumber || "";
    const lotNumber = item.LotNumber || "";
    const packing = item.Packing || "";
    const description = item.Description || "";

    const expiryDate = item.ExpiryDate
      ? new Date(item.ExpiryDate)
      : null;

    const expiryKey =
      expiryDate && !Number.isNaN(expiryDate.getTime())
        ? expiryDate.toISOString().slice(0, 10)
        : "";

    const key = [
      catalogNumber,
      lotNumber,
      packing,
      description,
      expiryKey,
    ].join("||");

    const deliveredQuantity = Number(item.QtyDelivered ?? 0) || 0;

    if (!map.has(key)) {
      map.set(key, {
        ...item,
        QtyDelivered: deliveredQuantity,
      });

      return;
    }

    const previousItem = map.get(key);

    previousItem.QtyDelivered =
      (Number(previousItem.QtyDelivered) || 0) + deliveredQuantity;

    if (item.ExpiryDate) {
      const previousExpiry = previousItem.ExpiryDate
        ? new Date(previousItem.ExpiryDate)
        : null;

      const currentExpiry = new Date(item.ExpiryDate);

      if (
        !Number.isNaN(currentExpiry.getTime()) &&
        (!previousExpiry ||
          Number.isNaN(previousExpiry.getTime()) ||
          currentExpiry < previousExpiry)
      ) {
        previousItem.ExpiryDate = item.ExpiryDate;
      }
    }

    map.set(key, previousItem);
  });

  return Array.from(map.values());
}

function splitItemsIntoPages(items, itemsPerPage) {
  if (!items.length) {
    return [[]];
  }

  const pages = [];

  for (let index = 0; index < items.length; index += itemsPerPage) {
    pages.push(items.slice(index, index + itemsPerPage));
  }

  return pages;
}

function DeliveryNoteHeader({ dn }) {
  return (
    <>
      <div className="dn-header-row">
        <div className="dn-logo-container">
          <img
            src="/img/biocare-logo.png"
            alt="Biocare Logo"
            className="dn-logo"
          />
        </div>

        <div className="dn-company">
          <strong>BIOCARE, INC.</strong>
          <br />
          Room 306 WEBJET Bldg., 64 Quezon Avenue,
          <br />
          B.M.A. Ave., Tatalon, Quezon City
          <br />
          Telephone: 8732-0535, 8712-0531
          <br />
          8714-9159, 8712-5389
          <br />
          Fax: (02) 8711-4310
          <br />
          VAT REG. TIN: 006-163-782-000
        </div>

        <div className="dn-header-right">
          <div className="dn-ref-label">REFER TO THIS NUMBER</div>

          <table className="dn-num-table">
            <thead>
              <tr>
                <th>DAY</th>
                <th>MO</th>
                <th>YR</th>
                <th>NUMBER</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>{getDatePart(dn.DNDate, "day")}</td>
                <td>{getDatePart(dn.DNDate, "month")}</td>
                <td>{getDatePart(dn.DNDate, "year")}</td>
                <td className="dn-number">{dn.DNNumber || ""}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="dn-title">DELIVERY NOTE</div>
    </>
  );
}

function DeliveryNoteCustomerInformation({ dn, so }) {
  return (
    <>
      <div className="dn-contact-row">
        <div className="contact-col">
          <div className="label">CUSTOMER</div>
          <div className="value">{so.CustomerName || ""}</div>
        </div>

        <div className="contact-col">
          <div className="label">SHIP TO</div>
          <div className="value">{so.ShipTo || ""}</div>
        </div>

        <div className="contact-col">
          <div className="label">MARK TO</div>
          <div className="value">{so.MarkTo || ""}</div>
        </div>
      </div>

      <table className="dn-customer-table">
        <tbody>
          <tr>
            <td>
              <div className="label">CUSTOMER NUMBER</div>
              <div className="value">{so.CustomerNumber || ""}</div>
            </td>

            <td>
              <div className="label">AREA CODE</div>
              <div className="value">{so.AreaManagerCode || ""}</div>
            </td>

            <td>
              <div className="label">REFERENCE ORDER</div>
              <div className="value">
                {so.ReferenceOrder || so.SONumber || ""}
              </div>
            </td>

            <td>
              <div className="label">DATE</div>
              <div className="value">{formatDate(dn.DNDate)}</div>
            </td>

            <td>
              <div className="label">SHIPPING INSTRUCTION</div>
              <div className="value">
                {so.ShippingInstruction || ""}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="dn-special">
        <div className="label">SPECIAL INSTRUCTION</div>

        <div className="value">
          {dn.SpecialInstruction || dn.special_instruction || "-"}
        </div>
      </div>
    </>
  );
}

function DeliveryNoteItemsTable({ items }) {
  return (
    <table className="dn-item-table">
      <thead>
        <tr>
          <th>QUANTITY</th>
          <th>UNIT OF MEASURE</th>
          <th>CATALOG NUMBER</th>
          <th>ITEM AND DESCRIPTION</th>
          <th>LOT NO</th>
          <th>EXPIRY DATE</th>
        </tr>
      </thead>

      <tbody>
        {items.length === 0 ? (
          <tr>
            <td colSpan={6} className="dn-no-items">
              NO ITEMS DELIVERED
            </td>
          </tr>
        ) : (
          items.map((item, index) => (
            <tr
              key={[
                item.CatalogNumber,
                item.LotNumber,
                item.ExpiryDate,
                index,
              ].join("-")}
            >
              <td className="dn-quantity-cell">
                {item.QtyDelivered}
              </td>

              <td>{item.Packing || ""}</td>

              <td>{item.CatalogNumber || ""}</td>

              <td className="dn-description-cell">
                {item.Description || ""}
              </td>

              <td>
                {item.LotNumber || ""}

                {item.LotNumber && Number(item.QtyDelivered) > 1
                  ? ` (${item.QtyDelivered})`
                  : ""}
              </td>

              <td>{formatDate(item.ExpiryDate)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function DeliveryNoteFooter({
  mode,
  copyType,
  showOverrideNote,
}) {
  return (
    <div className="dn-footer">
      {showOverrideNote && mode === "EDITED" && (
        <div className="dn-override-note">
          This is a PRINT OVERRIDE copy for customer use. Accounting
          and stock records remain based on the ORIGINAL delivery note.
        </div>
      )}

      <div className="dn-disclaimer">
        ANY LOSS OR DAMAGE DUE TO LEAKAGE, BREAKAGE, OR UNDETERMINED
        CAUSE AFTER DELIVERY OF SHIPMENT TO FORWARDER IS NOT OUR
        RESPONSIBILITY. SHIPMENT OF GOODS IS AT BUYER&apos;S RISK AND
        COST. GOODS REMAIN AS THE PROPERTY OF THE COMPANY UNTIL FULLY
        PAID.
      </div>

      <div className="dn-footer-row">
        <div className="dn-footer-left">
          <div className="f-line">
            <span>TRANSFER TO:</span>
            <span className="line" />
          </div>

          <div className="f-line">
            <span>INVOICE NO.</span>
            <span className="line" />
          </div>

          <div className="f-line">
            <span>DATE</span>
            <span className="line" />
          </div>
        </div>

        <div className="dn-footer-right">
          <div className="received-title">
            MERCHANDISE SPECIFIED ABOVE
            <br />
            WERE RECEIVED IN GOOD CONDITION.
          </div>

          <div className="signature-lines">
            <div className="line-long" />
            <div className="line-long" />
          </div>

          <div className="signature-labels">
            <span>PRINT NAME</span>
            <span>SIGNATURE</span>
          </div>

          <div className="date-group">
            <div className="date-line" />
            <div className="date-label">DATE</div>
          </div>
        </div>
      </div>

      <div className="dn-copy-label">{copyType}</div>
    </div>
  );
}

export default function DnPrintTemplate({
  dn,
  so,
  items,
  mode,
  copyType = "CUSTOMER'S COPY",
  showOverrideNote = true,
}) {
  if (!dn || !so) {
    return null;
  }

  const groupedItems = groupDeliveryItems(items);
  const pages = splitItemsIntoPages(
    groupedItems,
    ITEMS_PER_PAGE
  );

  return (
    <div className="dn-print-document">
      {pages.map((pageItems, pageIndex) => {
        const pageNumber = pageIndex + 1;
        const totalPages = pages.length;
        const isLastPage = pageNumber === totalPages;

        return (
          <section
            className="dn-page dn-print-page"
            key={`delivery-note-page-${pageNumber}`}
          >
            <div className="dn-print">
              <DeliveryNoteHeader dn={dn} />

              <DeliveryNoteCustomerInformation
                dn={dn}
                so={so}
              />

              <DeliveryNoteItemsTable items={pageItems} />

              {isLastPage ? (
                <DeliveryNoteFooter
                  mode={mode}
                  copyType={copyType}
                  showOverrideNote={showOverrideNote}
                />
              ) : (
                <div className="dn-continuation">
                  CONTINUED ON NEXT PAGE
                </div>
              )}

              <div className="dn-page-number">
                PAGE {pageNumber} OF {totalPages}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
